import React from 'react';
import { ArrowUpRight, Phone, Mail } from 'lucide-react';
import { businessInfo } from '../data/mock';
import useReveal from '../hooks/useReveal';
import JourneyStrip from './JourneyStrip';

const CTA_IMG =
  'https://images.unsplash.com/photo-1732375014952-917bc49a4f8a?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1400';

const journeySteps = [
  { id: 'discover', label: 'Discover' },
  { id: 'curate', label: 'Curate' },
  { id: 'reserve', label: 'Reserve' },
];

const BookingSystem = () => {
  const imgReveal = useReveal();
  const contentReveal = useReveal();

  return (
    <section
      id="booking"
      data-testid="booking-section"
      className="relative py-28 md:py-36 bg-carbon overflow-hidden grain"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-14 lg:gap-20 items-stretch">
          {/* Image */}
          <div
            ref={imgReveal.ref}
            className={`reveal ${imgReveal.visible ? 'is-visible' : ''} lg:col-span-5 order-2 lg:order-1`}
          >
            <div className="relative aspect-[4/5] rounded-sm overflow-hidden border border-pewter/60 h-full min-h-[460px]">
              <img
                src={CTA_IMG}
                alt="Editorial body sculpting transformation"
                className="absolute inset-0 w-full h-full object-cover editorial-img"
                loading="lazy"
              />
              <div
                aria-hidden="true"
                className="absolute inset-0"
                style={{
                  background:
                    'linear-gradient(180deg, rgba(10,10,11,0.15) 0%, rgba(10,10,11,0.75) 100%)',
                }}
              />
              <div className="absolute bottom-8 left-8 right-8">
                <p className="brand-italic chrome-rose text-sm mb-2">A private beginning.</p>
                <p className="font-serif text-ivory text-2xl leading-snug">
                  Your transformation, curated from the very first consultation.
                </p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div
            ref={contentReveal.ref}
            className={`reveal ${contentReveal.visible ? 'is-visible' : ''} lg:col-span-7 order-1 lg:order-2 flex flex-col justify-center`}
            style={{ '--reveal-delay': '120ms' }}
          >
            <p className="eyebrow mb-6">
              <span className="inline-block w-8 h-px bg-silverx align-middle mr-3" />
              The Invitation
            </p>
            <h2 className="font-serif text-ivory text-4xl sm:text-5xl lg:text-[60px] leading-[1.06] mb-8">
              Begin Your <span className="brand-italic chrome-rose">Transformation</span>
            </h2>
            <p className="text-platinum text-base md:text-lg leading-relaxed max-w-xl mb-10">
              Your goals deserve more than generic treatments. Experience personalized
              luxury care designed around your body, your wellness, and your desired results.
            </p>

            {/* Journey Strip — editorial wayfinder */}
            <div className="mb-10 max-w-md">
              <JourneyStrip steps={journeySteps} tone="forward" />
            </div>

            {/* Primary CTA */}
            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <a
                href={businessInfo.bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="booking-primary-cta"
                className="btn-pill group inline-flex items-center gap-3 px-8 py-4 rounded-full bg-ivory text-onyx text-[11px] tracking-[0.28em] uppercase font-medium hover:bg-blush"
              >
                Book Consultation
                <ArrowUpRight
                  size={16}
                  className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
                />
              </a>
              <a
                href={`tel:${businessInfo.phone}`}
                data-testid="booking-phone-cta"
                className="btn-pill inline-flex items-center gap-3 px-8 py-4 rounded-full border border-pewter text-ivory text-[11px] tracking-[0.26em] uppercase hover:border-blush hover:text-blush"
              >
                <Phone size={14} /> {businessInfo.phone}
              </a>
            </div>

            {/* Details row */}
            <div className="grid sm:grid-cols-3 gap-8 pt-10 border-t border-pewter/60">
              <div>
                <p className="eyebrow mb-3">Studio Hours</p>
                <p className="text-ivory text-sm leading-relaxed">
                  {businessInfo.hours.weekday}
                  <br />
                  {businessInfo.hours.weekend}
                </p>
              </div>
              <div>
                <p className="eyebrow mb-3">Address</p>
                <p className="text-ivory text-sm leading-relaxed">{businessInfo.address}</p>
              </div>
              <div>
                <p className="eyebrow mb-3">Concierge</p>
                <a
                  href={`mailto:${businessInfo.email}`}
                  className="text-ivory text-sm hover:text-blush inline-flex items-center gap-2 transition-colors"
                >
                  <Mail size={13} /> {businessInfo.email}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BookingSystem;
